<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            Las búsquedas de «crear tour virtual inmobiliario» han crecido un
            <strong>+4 400 %</strong> en los últimos tres años. Las
            publicaciones con tour virtual reciben un 87 % más de visitas. Sin
            embargo, muchos agentes siguen creyendo que necesitan un Matterport
            de 3.000 € o un fotógrafo profesional. Ya no es así — aquí tienes
            cómo crear tu tour virtual inmobiliario con tu smartphone.
          </p>
        </section>

        <section>
          <h2>Lo que realmente necesitas</h2>

          <h3>Equipamiento mínimo</h3>
          <ul>
            <li>iPhone 13+ o Android equivalente (menos de 4 años)</li>
            <li>Aplicación Travvir (prueba gratuita)</li>
            <li>Luz natural o lámparas de apoyo</li>
            <li>Trípode opcional (mejora la uniformidad)</li>
          </ul>

          <h3>Sin equipamiento caro</h3>
          <p>
            Las cámaras 360° dedicadas (Ricoh Theta, Insta360) cuestan 300–700
            €. Un kit Matterport supera los 3.000 €. Travvir convierte tu
            smartphone existente en una herramienta de tour virtual profesional
            sin inversión en hardware adicional.
          </p>
        </section>

        <section>
          <h2>Preparar la propiedad antes de la sesión</h2>

          <p>
            La preparación representa el 80 % de la calidad final. Sigue esta
            checklist antes de cada rodaje:
          </p>

          <h3>Despejar el espacio</h3>
          <p>
            Guarda los objetos personales (fotos familiares, correo,
            medicamentos). Los compradores deben proyectarse — no ver la vida
            del vendedor. Libera encimeras y superficies visibles.
          </p>

          <h3>Luz óptima</h3>
          <p>
            Abre todas las persianas y cortinas al máximo. Enciende todas las
            luces interiores — incluso a plena luz del día. Evita los días muy
            soleados que crean halos a través de las ventanas.
          </p>

          <h3>Puesta en escena rápida</h3>
          <p>
            Coloca los muebles desplazados en su posición natural. Añade flores
            frescas o un cuenco de fruta en la cocina. Estos detalles cuestan
            menos de 20 € y mejoran significativamente las conversiones.
          </p>
        </section>

        <section>
          <h2>Proceso de rodaje paso a paso</h2>

          <h3>1. Abrir Travvir y crear un proyecto</h3>
          <p>
            Abre la aplicación, pulsa «Nuevo proyecto» y pon un nombre a tu
            propiedad (ej.: «Calle Mayor 12 — 28001 Madrid»). Selecciona el tipo
            de propiedad en el menú desplegable.
          </p>

          <h3>2. Capturar cada habitación en orden</h3>
          <p>
            Empieza por el exterior si la propiedad lo tiene, luego avanza
            lógicamente: entrada → salón → cocina → dormitorios → baños.
            Colócate en el centro de cada habitación, sujeta el smartphone a la
            altura de los ojos y gira siguiendo las instrucciones de la
            aplicación.
          </p>

          <h3>3. Añadir escenas adicionales si es necesario</h3>
          <p>
            Para habitaciones grandes en L o espacios abiertos, añade una
            segunda escena de ángulo. Esto elimina las zonas muertas invisibles
            desde un solo punto de vista.
          </p>

          <h3>4. Dejar que Travvir ensamble el tour</h3>
          <p>
            Sube tus capturas — Travvir ensambla automáticamente cada panorama y
            crea los enlaces de navegación entre escenas. El procesado tarda 2 a
            5 minutos por escena.
          </p>

          <h3>5. Previsualizar y pulir</h3>
          <p>
            Revisa cada transición, corrige la orientación del panorama inicial
            si es necesario y añade los hotspots de navegación que falten.
          </p>
        </section>

        <section>
          <h2>Publicación y distribución</h2>
          <p>
            Una vez satisfecho con el resultado, publica el tour. Obtienes al
            instante un enlace directo y un código iFrame. Compártelos en tu
            anuncio MLS, en tu web, por correo a compradores potenciales y en
            redes sociales.
          </p>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "crear-tour-virtual-inmobiliario",
);

useBlogSeo({
  title: "Cómo Crear un Tour Virtual Inmobiliario con tu Smartphone | Travvir",
  description:
    "Crea un tour virtual inmobiliario profesional con tu smartphone. Checklist de preparación, guía de rodaje paso a paso y consejos de publicación.",
  keywords:
    "crear tour virtual inmobiliario, tour virtual smartphone inmobiliario, guía tour virtual agente",
  slug: "crear-tour-virtual-inmobiliario",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question:
        "¿Se necesita una cámara 360° para crear un tour virtual inmobiliario?",
      answer:
        "No — un iPhone 13+ o Android reciente es suficiente con la aplicación Travvir. No se necesita equipamiento caro.",
    },
    {
      question: "¿Cuánto tiempo se tarda en crear un tour completo?",
      answer:
        "Cuenta con 30 a 60 minutos en la propiedad para un piso de 3 habitaciones, más 10 a 20 minutos de procesado automático.",
    },
  ],
});

</script>
